import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAuth } from '../../auth/AuthContext';
import * as api from '../plannerApi';
import * as T from '../types';
import { EXPENSE_KINDS, START_SCREENS, THEMES } from '../labels';
import { PageHeader, Panel, UserAvatar, useHousehold, useLoad, useNotify, useRun } from '../common';

const PHOTO_SIDE = 512;
const MIN_PASSWORD = 8;

/** Reduces the picked image to at most 512 px on its longer side, as JPEG in base64 (what the server keeps). */
function reducePhoto(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, PHOTO_SIDE / Math.max(image.naturalWidth, image.naturalHeight));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(image.naturalWidth * scale);
      canvas.height = Math.round(image.naturalHeight * scale);
      canvas.getContext('2d')!.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.85).split(',')[1]);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Não foi possível abrir essa imagem.'));
    };
    image.src = url;
  });
}

/** "A minha conta": photo, name, settings, password and shopping account, as the Android ProfilePage. */
const AccountPage: React.FC = () => {
  const { profile, setProfile, settings, setSettings, login } = useAuth();
  const run = useRun();
  const { reload: reloadHousehold } = useHousehold();
  const notify = useNotify();
  const fileInput = useRef<HTMLInputElement>(null);
  const [name, setName] = useState('');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const accounts = useLoad(() => api.getExpenseAccounts()
    .then(list => list.slice().sort((a, b) => a.name.localeCompare(b.name, 'pt-PT', { sensitivity: 'base' }))));

  useEffect(() => { setName(profile?.name ?? ''); }, [profile?.name]);

  const displayName = profile?.name || profile?.email || '';

  const saveName = async (e: React.FormEvent) => {
    e.preventDefault();
    const saved = await run(() => api.updateProfile(name.trim() || null), 'Nome guardado');
    if (saved) setProfile(saved);
  };

  const pickPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    const saved = await run(async () => api.setPhoto(await reducePhoto(file)), 'Foto mudada');
    setBusy(false);
    if (saved) setProfile(saved);
  };

  const removePhoto = async () => {
    const saved = await run(() => api.deletePhoto(), 'Foto tirada');
    if (saved) setProfile(saved);
  };

  const chooseShoppingAccount = async (accountPublicId: string) => {
    const saved = await run(async () => ({ chosen: await api.setShoppingAccount(accountPublicId || null) }));
    if (!saved) return;
    accounts.setData(list => list?.map(a => ({ ...a, isShoppingDefault: a.publicId === saved.chosen })));
    notify(saved.chosen ? 'Conta das compras guardada' : 'Sem conta das compras', { severity: 'success' });
  };
  const saveSettings = async (change: Partial<T.UpdateSettingsRequest>, done: string) => {
    if (!settings) return;
    const request: T.UpdateSettingsRequest = {
      theme: settings.theme,
      startScreen: settings.startScreen,
      defaultExpenseType: settings.defaultExpenseType,
      memberPublicId: null,
      ...change,
    };
    const saved = await run(() => api.updateSettings(request), done);
    if (!saved) return;
    setSettings(saved);
    // Another member is now "you": the household's members change too
    if (change.memberPublicId) await reloadHousehold();
  };

  const shoppingAccount = accounts.data?.find(a => a.isShoppingDefault)?.publicId ?? '';

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const problem = !current ? 'Escreve a palavra-passe atual.'
      : next.length < MIN_PASSWORD ? `A palavra-passe nova tem de ter pelo menos ${MIN_PASSWORD} caracteres.`
      : next !== repeat ? 'As duas palavras-passe novas não são iguais.'
      : null;
    setPasswordError(problem);
    if (problem) return;
    setBusy(true);
    try {
      const result = await api.changePassword(current, next);
      login(result.token);
      setCurrent('');
      setNext('');
      setRepeat('');
      notify('Palavra-passe mudada. As outras sessões terminaram.', { severity: 'success' });
    } catch (err: any) {
      setPasswordError(err.message ?? 'Ocorreu um erro.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="A minha conta" subtitle={profile?.email} />
      <Stack spacing={2} sx={{ maxWidth: 560 }}>
        <Panel title="Foto e nome">
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <UserAvatar name={displayName} photoUrl={profile?.photoUrl} size={88} />
            <Stack spacing={1} alignItems="flex-start">
              <input ref={fileInput} type="file" accept="image/*" hidden onChange={pickPhoto} />
              <Button variant="outlined" startIcon={<PhotoCameraOutlinedIcon />} disabled={busy} onClick={() => fileInput.current?.click()}>
                {profile?.photoUrl ? 'Mudar a foto' : 'Pôr foto'}
              </Button>
              {profile?.photoUrl && (
                <Button color="error" startIcon={<DeleteOutlineIcon />} disabled={busy} onClick={removePhoto}>Tirar a foto</Button>
              )}
            </Stack>
          </Stack>
          <Box component="form" onSubmit={saveName} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField label="Nome" placeholder="Como te chamam na casa" value={name} onChange={e => setName(e.target.value)}
              inputProps={{ maxLength: 100 }} size="small" fullWidth />
            <Button type="submit" variant="contained" sx={{ flexShrink: 0 }}>Guardar</Button>
          </Box>
        </Panel>

        {settings && (
          <Panel title="Preferências">
            <Stack spacing={2}>
              {settings.members.length > 1 && (
                <TextField select label="Quem és na casa" size="small" fullWidth value={settings.memberPublicId ?? ''}
                  onChange={e => saveSettings({ memberPublicId: e.target.value }, 'Membro da casa mudado')}
                  helperText="As tarefas e as refeições de quem escolheres passam a ser tuas.">
                  {settings.members.map(m => <MenuItem key={m.publicId} value={m.publicId}>{m.name}</MenuItem>)}
                </TextField>
              )}
              <TextField select label="Tema" size="small" fullWidth value={settings.theme}
                onChange={e => saveSettings({ theme: e.target.value as T.ThemePreference }, 'Tema guardado')}>
                {THEMES.map(t => <MenuItem key={t.value} value={t.value}>{t.text}</MenuItem>)}
              </TextField>
              <TextField select label="Ecrã inicial" size="small" fullWidth value={settings.startScreen}
                onChange={e => saveSettings({ startScreen: e.target.value as T.StartScreen }, 'Ecrã inicial guardado')}
                helperText="Onde a web e a app Android abrem depois de entrar.">
                {START_SCREENS.map(s => <MenuItem key={s.value} value={s.value}>{s.text}</MenuItem>)}
              </TextField>
              <TextField select label="Despesas por omissão" size="small" fullWidth value={settings.defaultExpenseType}
                onChange={e => saveSettings({ defaultExpenseType: e.target.value as T.ExpenseKind }, 'Despesas por omissão guardadas')}
                helperText="As que aparecem primeiro nas despesas.">
                {EXPENSE_KINDS.map(k => <MenuItem key={k.value} value={k.value}>{k.text}</MenuItem>)}
              </TextField>
            </Stack>
          </Panel>
        )}

        {accounts.data && accounts.data.length > 0 && (
          <Panel title="Compras">
            <TextField select label="Conta das compras" size="small" fullWidth value={shoppingAccount}
              onChange={e => chooseShoppingAccount(e.target.value)}
              helperText="A que vem escolhida quando pagas uma ida às compras.">
              <MenuItem value=""><em>Nenhuma (vem a conta principal)</em></MenuItem>
              {accounts.data.map(a => <MenuItem key={a.publicId} value={a.publicId}>{a.name}</MenuItem>)}
            </TextField>
          </Panel>
        )}

        <Panel title="Mudar a palavra-passe">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            As sessões nos outros dispositivos terminam: aí pede-se a palavra-passe nova.
          </Typography>
          <Stack component="form" spacing={1.5} onSubmit={changePassword}>
            <TextField label="Palavra-passe atual" type="password" autoComplete="current-password" size="small"
              value={current} onChange={e => setCurrent(e.target.value)} />
            <TextField label="Palavra-passe nova" type="password" autoComplete="new-password" size="small"
              helperText={`Pelo menos ${MIN_PASSWORD} caracteres`} value={next} onChange={e => setNext(e.target.value)} />
            <TextField label="Repete a nova" type="password" autoComplete="new-password" size="small"
              value={repeat} onChange={e => setRepeat(e.target.value)} />
            {passwordError && <Typography variant="body2" color="error">{passwordError}</Typography>}
            <Box><Button type="submit" variant="contained" disabled={busy}>Mudar</Button></Box>
          </Stack>
        </Panel>
      </Stack>
    </>
  );
};

export default AccountPage;
